import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, shareReplay, skip, takeUntil, tap } from 'rxjs/operators';
import { AppService, ProductTranslations } from './app.service';
import { ProductsCategoryService } from './products-category.service';
import { Quote, QuoteService } from './quote.service';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { EnvironmentDiffService } from './environment-diff.service';
import { isEqual } from 'lodash';
import { OfferService } from './offer.service';
import { OfferItem, OfferPrintOptions } from '../models/offer.model';
import { isGroupWithHintText } from '../utils/offer.util';

export interface ProductGroup {
    name: string;
    translationKey: string;
    order?: number;
    products: Product[];
    printOptions?: OfferPrintOptions; // only for sample offers
}

export interface ProductHeadline {
    groupNameTranslation: string;
    children: string[];
}

export interface Product {
    order?: number;
    material: string;
    shortText?: string;
    quantity: number;
    category?: string;
    translation?: ProductTranslations;
    materialGroup?: string;
    canBeOptional?: boolean;
    optional?: boolean;
    alternatives?: Product[]; // only for sample offers
    alternativeFor?: string; // only for sample offers
    printOptions?: OfferPrintOptions; // only for sample offers
    selected: boolean;
}

export interface PriceSummary {
    totalGross: number;
    discount: number;
    totalPrice: number;
    netPrice: number;
    vat: number;
    sum: number;
    voucherValue: number;
}

export interface AssetConversion {
    conversionId: string;
    url: string;
    resolution: string;
    format: string;
}

export interface Asset {
    _id: string;
    type: string;
    name: string;
    assetConversions: AssetConversion[];
}

export interface AssetReference {
    referenceTypeId: string;
    assets: Asset[];
}

export interface TechnicalAttribute {
    attributeId: string;
    values: Value[];
}

export interface Value {
    value: string;
    valueId: string;
}

export interface GeneralData {
    materialPriceGroup: Value;
}

export interface PimPrice {
    price: {
        value: number;
        unit: string;
        unitId: string; // e.g. 'iso4217.unit.EUR'
    };
}

export interface PimProduct {
    _id: string;
    name: string;
    type: string;
    description: string;
    assetReferences: AssetReference[];
    technicalAttributes: TechnicalAttribute[];
    generalData: GeneralData;
    price: PimPrice;
}

export interface PimProductInfo {
    name: string;
    description: string;
    categoryName: string;
    materialGroup: string;
    locale: string;
    currency: string;
}

interface ProductCategoryGroup {
    'phraseapp-key': string;
    categories: { [key: string]: number }[];
    order: number;
}

interface ProductCategoryGroupMap {
    [key: string]: ProductCategoryGroup;
}

interface OfferItemWithProductInfo extends OfferItem {
    info: PimProductInfo;
}

@Injectable({
    providedIn: 'root',
})
export class ProductsService {
    private unsubscribe$: Subject<void> = new Subject<void>();
    constructor(
        private http: HttpClient,
        private quoteService: QuoteService,
        private appService: AppService,
        private productCategory: ProductsCategoryService,
        private environmentDiffService: EnvironmentDiffService,
        private offerService: OfferService
    ) {
        this.subscribeToLanguageChanged$();
        this.materialCache = new Map();
        this.materialRequests = new Map();
        this.productImageCache = new Map();

        this.quoteService
            .getCurrentQuoteObservable()
            .pipe(
                skip(1),
                filter((v) => v === undefined || v.isNew)
            )
            .subscribe(() => {
                this.materialCache.clear();
                this.materialRequests.clear();
            });
    }
    private materialCache: Map<string, PimProductInfo>;
    private materialRequests: Map<string, Observable<string>>;
    private productImageCache: Map<string, string | undefined>;

    private priceSummaryCache: PriceSummary;

    public productCategoriesGroupNamesMap: ProductCategoryGroupMap = {};

    public getProductImage(material: string) {
        if (this.materialRequests.has(material)) {
            return this.materialRequests.get(material);
        }
        if (this.productImageCache.has(material)) {
            const img = this.productImageCache.get(material);
            return of(img ?? '../../../../assets/images/empty_image.png');
        }
        this.materialRequests.set(material, this.fetchProductImage(material));
        return this.materialRequests.get(material);
    }

    public addPriceSummaryToCache(
        totalGross: number,
        discount: number,
        totalPrice: number,
        netPrice: number,
        vat: number,
        sum: number,
        voucherValue: number
    ) {
        if (!this.priceSummaryCache) {
            this.priceSummaryCache = {
                totalGross: 0,
                discount: 0,
                totalPrice: 0,
                netPrice: 0,
                vat: 0,
                sum: 0,
                voucherValue: 0,
            };
        }
        this.priceSummaryCache.totalGross = totalGross;
        this.priceSummaryCache.discount = discount;
        this.priceSummaryCache.totalPrice = totalPrice;
        this.priceSummaryCache.netPrice = netPrice;
        this.priceSummaryCache.vat = vat;
        this.priceSummaryCache.sum = sum;
        this.priceSummaryCache.voucherValue = voucherValue;
    }

    public clearPriceSummaryCache() {
        this.priceSummaryCache = undefined;
        this.materialCache.clear();
        this.materialRequests.clear();
    }

    public getProducts(
        materials: { material: string; quantity: number; bomItemId?: string }[],
        quote: Quote,
        checkOptional = false
    ): Observable<Product[]> {
        const materialNumbers = materials.map((m) => m.material);
        return forkJoin([
            this.getMaterialInfos(materialNumbers),
            checkOptional ? this.getOptionalMaterials(materialNumbers) : of(<string[]>[]),
        ]).pipe(
            tap(([materialInfos]) => this.updateCurrencyInQuote(quote, materialInfos)),
            map(([materialInfos, optionals]) => ({
                mappedMaterials: materials
                    .map((m) => ({
                        ...m,
                        info: materialInfos.find((mi) => mi.name === m.material),
                    }))
                    .filter((m) => !!m.info),
                optionals,
            })),
            tap(({ mappedMaterials }) =>
                mappedMaterials
                    .filter(({ bomItemId }) => !!bomItemId)
                    .forEach((material) =>
                        this.quoteService.ensureSubLine(
                            material.bomItemId,
                            material.quantity,
                            material.info,
                            quote.quote
                        )
                    )
            ),
            map(({ mappedMaterials, optionals }) =>
                mappedMaterials.map(({ material, quantity, info }) => {
                    const { categoryName, materialGroup, description, locale } = info;

                    const translation = {
                        [locale]: description,
                    };

                    const canBeOptional = optionals.includes(material);
                    const optionalCategories = this.environmentDiffService.getOptionalCategoriesFromCache();
                    const defaultOptional = canBeOptional && optionalCategories.includes(categoryName);

                    return <Product>{
                        material,
                        category: categoryName,
                        materialGroup,
                        translation,
                        quantity,
                        canBeOptional,
                        optional: defaultOptional,
                        selected: true,
                    };
                })
            )
        );
    }

    public getMaterialInfos(materials: string[], hasImage = true): Observable<PimProductInfo[]> {
        const cached: PimProductInfo[] = [];
        // collect all cached materials
        materials.forEach((material) => {
            if (this.materialCache.has(material)) {
                cached.push(this.materialCache.get(material));
            } else if (hasImage) {
                this.fetchProductImage(material).subscribe(); // just fetch and cache, no need to care about result here
            }
        });
        if (
            isEqual(
                materials,
                cached.map((c) => c.name)
            )
        ) {
            return of(cached);
        }
        const url = `${environment.http.pim}products/${this.appService.getLanguageOnly()}/${this.appService
            .getLocation()
            .toLowerCase()}`;
        // fetch only material infos that are not in cache
        const materialIds = materials.filter((name) => !cached.some((m) => m.name === name));

        return this.http
            .post<PimProduct[]>(
                url,
                {
                    products: materialIds,
                    attributes: ['AT_SAP_V_KATEGORIE_QA'],
                },
                {
                    params: {
                        dataGroups: ['generalData', 'attributes', 'price'],
                        strict: true,
                    },
                }
            )
            .pipe(
                map((products) => products.map((p) => this.getPimProductInfo(p))),
                tap((products) => products.forEach((product) => this.materialCache.set(product.name, product))),
                map((products) => [...cached, ...products]) // combine cached and retrieved data
            );
    }

    public getProductsFromSampleOffer(offerId: string, quote: Quote): Observable<ProductGroup[]> {
        return this.offerService.getOffer(offerId).pipe(
            map((offer) => offer.items),
            concatMap((items) => {
                const allItems = items.reduce<OfferItem[]>(
                    (prev, curr) => [...prev, ...(<OfferItem[]>curr.alternatives), curr],
                    []
                );
                const materials = allItems.map((i) => i.material).filter(Boolean);
                this.quoteService.pruneSubLines(materials, quote.quote);
                return this.getMaterialInfos(materials).pipe(
                    tap((infos) => {
                        this.updateCurrencyInQuote(quote, infos);
                        infos.forEach((info) => {
                            const item = allItems.find((i) => i.material === info.name);
                            this.quoteService.ensureSubLine(`${item.itemNumber}`, item.quantity, info, quote.quote);
                        });
                    }),
                    map((infos) => {
                        const extend: (item: OfferItem) => OfferItemWithProductInfo = (item: OfferItem) => ({
                            ...item,
                            info: item.material ? infos.find((info) => info.name === item.material) : undefined,
                            alternatives: item.alternatives.map(extend),
                        });
                        return items.map(extend);
                    })
                );
            }),
            map((items) => {
                let index = 1;
                const otherGroup: ProductGroup = this.createOtherProductGroup();
                let currentGroup: ProductGroup = otherGroup;
                const toProduct = (item: OfferItemWithProductInfo) => {
                    const { categoryName, materialGroup, description, locale } = item.info;
                    const alternatives: Product[] = item.alternatives.map(toProduct).map((alt) => ({
                        ...alt,
                        alternativeFor: item.material,
                        canBeOptional: false,
                        optional: false,
                        selected: false,
                    }));
                    return <Product>{
                        material: item.material,
                        quantity: item.quantity,
                        category: categoryName,
                        optional: item.optional,
                        selected: true,
                        materialGroup,
                        translation: { [locale]: description },
                        canBeOptional: item.optional,
                        printOptions: item.printOptions,
                        alternatives,
                    };
                };
                const productGroups = items.reduce((prev: ProductGroup[], curr) => {
                    if (curr.material) {
                        // insert product in current group
                        const product = toProduct(curr);
                        currentGroup.products.push(product, ...product.alternatives);
                        return prev;
                    } else {
                        // create new product group
                        const group = {
                            name: curr.shortText,
                            order: index++,
                            translationKey: curr.shortText,
                            products: [],
                            printOptions: curr.printOptions,
                        };

                        // do not insert products in hint groups
                        if (!isGroupWithHintText(group)) {
                            currentGroup = group;
                        }

                        return [...prev, group];
                    }
                }, <ProductGroup[]>[]);
                otherGroup.products = this.sortOtherProducts(otherGroup.products);
                return [...productGroups, otherGroup];
            })
        );
    }

    public createOtherProductGroup(products: Product[] = []): ProductGroup {
        return {
            name: 'otherGroup',
            translationKey: 'COMMON.CATEGORIES.OTHER_PRODUCT_GROUP',
            products: this.sortOtherProducts(products),
        };
    }
    public sortOtherProducts(products: Product[]): Product[] {
        return products.sort((a: Product, b: Product) => {
            const textA = this.chooseTranslation(a.translation).toUpperCase();
            const textB = this.chooseTranslation(b.translation).toUpperCase();
            return textA.localeCompare(textB);
        });
    }

    private updateCurrencyInQuote(quote: Quote, infos: PimProductInfo[]) {
        const currency = infos.map((i) => i.currency).filter(Boolean)[0];
        this.quoteService.updateCurrency(currency, quote);
    }

    private getPimProductInfo(product: PimProduct): PimProductInfo {
        const categoryName = product.technicalAttributes?.find(
            (attribute) => attribute.attributeId === 'AT_SAP_V_KATEGORIE_QA'
        )?.values[0].valueId;
        const materialGroup = product.generalData?.materialPriceGroup?.valueId;
        const locale = `${this.appService.getLanguageOnly()}-${this.appService.getLocation().toUpperCase()}`;
        const currency = product.price?.price?.unitId?.split('.')?.pop() || '';

        return <PimProductInfo>{
            name: product.name,
            description: product.description,
            categoryName,
            materialGroup,
            locale,
            currency,
        };
    }

    public fetchProductImage(material: string): Observable<string | undefined> {
        const url = `${environment.http.pim}products/${this.appService.getLanguageOnly()}/${this.appService
            .getLocation()
            .toLowerCase()}/assets`;
        const conversionId = 'AT_AssetPush_Conversion_URL_PNG_210';

        return this.http
            .post<PimProduct[]>(url, {
                products: [material],
            })
            .pipe(
                catchError(() => of(undefined)),
                map((response: PimProduct[] | undefined) => {
                    return response?.[0]?.assetReferences
                        ?.map((assetRef: AssetReference) =>
                            assetRef.assets?.map((asset: Asset) =>
                                asset.assetConversions?.find(
                                    (conv: AssetConversion) => conv.conversionId === conversionId
                                )
                            )
                        )
                        .flat()
                        .find((conversion) => conversion?.url)?.url;
                }),
                tap((imgUrl) => {
                    this.productImageCache.set(material, imgUrl);
                    this.materialRequests.delete(material);
                }),
                shareReplay()
            );
    }

    public chooseTranslation(productTranslations?: ProductTranslations) {
        return this.appService.chooseTranslation(productTranslations);
    }

    public getOptionalMaterials(materials: string[]): Observable<string[]> {
        return this.http
            .post<string[]>(
                `${environment.http.baseUrl}material/optional`,
                {
                    materials,
                },
                {
                    params: {
                        salesOrg: this.appService.getSalesOrg(),
                    },
                }
            )
            .pipe(catchError(() => of([])));
    }

    private subscribeToLanguageChanged$(): void {
        this.appService.salesOrg$.pipe(takeUntil(this.unsubscribe$), filter(Boolean)).subscribe((salesOrg: string) => {
            this.productCategory.fetchProductCategoryGroupNameMap(salesOrg).subscribe((res) => {
                this.productCategoriesGroupNamesMap = res['product-category-group-map'];
            });
        });
    }
}
